/* eslint-disable camelcase */
import { useEffect, useState } from "react";
import axios from "axios";
import { js_settings } from "../utils/legacy";
import cropr, { BackboneModel } from "../utils/cropr";
import _ from "underscore";

interface ReferenceFeature {
  geometry: any;
  id: string;
  properties: {
    area: number;
    code: number;
    name: string;
    edi_code?: string;
  };
}

interface ReferenceResponse {
  features: ReferenceFeature[];
}
const reSource = /^GR-([\d\w]+)-/;

interface AggregateResult {
  name: string;
  area: number;
  edi_code?: string;
}
type SourceAggregate = { [source: string]: Record<string, AggregateResult> };

type ModelAttributes = { [attribute: string]: any };

function toModelAttributes(data: SourceAggregate): ModelAttributes {
  const result: any = {};
  for (let [source, perCode] of Object.entries(data)) {
    // find code with max total area
    const [, { edi_code }] = Object.entries(perCode).reduce((a, b) =>
      a[1].area > b[1].area ? a : b
    );
    switch (source) {
      case "NLS06":
        result.soil_type = _.findWhere(Object.values(cropr.soiltype), { edi_code })?.id;
        break;
      case js_settings.REG_SOIL_TYPE_MAP_VERSION:
      case "NLS13":
        result.regulatory_soil_type = _.findWhere(Object.values(cropr.soiltype), { edi_code })?.id;
        break;
      case "NLGWP":
        const t = _.findWhere(Object.values(cropr.fieldattributetype), {
          edi_code: "groundwater_p",
        });
        if (t) {
          if (!result.field_attributes) result.field_attributes = {};
          result.field_attributes[t.id] = true;
        }
        break;
    }
  }
  return result;
}

function perSource(response: ReferenceResponse): SourceAggregate {
  const result: SourceAggregate = {};
  response.features.forEach(({ id, properties }) => {
    const source = reSource.exec(id)?.[1];
    if (!source) return;
    const { name, code, area, edi_code } = properties;
    const res = result[source] || (result[source] = {});
    const cat: AggregateResult = res[code] || (res[code] = { name, area: 0, edi_code });
    cat.area += area;
  });
  return result;
}

export const useReferenceData = (model: BackboneModel) => {
  const geometry = model.get("geometry");
  const dontFetch = model.id || !(geometry && cropr.features.soil_data);
  const [data, setData] = useState<ModelAttributes>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (dontFetch) return;
    let cancelled = false;
    setLoading(true);
    axios
      .post<ReferenceResponse>(js_settings.GEO_LAYER_URL + "/datareference/intersect", geometry, {
        headers: js_settings.JWT_REFERENCE,
      })
      .then((res) => {
        if (cancelled) return;
        setData(toModelAttributes(perSource(res.data)));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dontFetch, model.id, geometry]);

  return { data, loading, error };
};
